// Row contracts and jsonb shapes of spaces, chat, inbox, boards and tasks (M2-01; 02 §Spaces,
// channels, messages, §Boards and tasks). Same conventions as `rows.ts`: keys are column names,
// nullability is the column's, enums are the CHECK's; `contract-drift.test.ts` binds each to its
// table. The jsonb shapes are the Drizzle `$type`s.
import { z } from "zod";
import { HELPER_KPI_ROLES, type HelperKpiRole } from "../helper-metric-catalog";
import { ActorColumns, ActorShortColumns } from "./actor";
import {
  AttachmentState,
  AttachmentSubjectKind,
  BoardColor,
  BoardDigestInterval,
  ChannelKind,
  ChannelMemberRole,
  ChannelNotifyLevel,
  ChannelPostingPolicy,
  ChatAskState,
  ChatBroadcast,
  Currency,
  DigestFrequency,
  FieldAudience,
  FieldType,
  FollowLevel,
  FollowSubjectKind,
  InboxItemKind,
  InboxSubjectKind,
  LinkPreviewStatus,
  MessageAuthorKind,
  NotificationCategory,
  NotificationPreset,
  ReplyLength,
  ScheduledMessageState,
  SpaceKind,
  TaskDependencyKind,
  TaskOrigin,
  ThreadKind,
  ThreadParticipantRole,
  TopicMemberVia,
  TopicStatus,
  ViewKind,
} from "./enums";
import { boundedRecord, LIMITS, Timestamp, text, Uuid } from "./primitives";

const actor = ActorShortColumns.shape;
const stamps = { created_at: Timestamp, updated_at: Timestamp };

// jsonb shapes ----------------------------------------------------------------------------------

/**
 * BlockNote document JSON (04 §Base rules): an array of blocks. The API also returns
 * `body_text` and accepts `body_markdown` on write, so agents never need to emit this.
 */
export const BlockNoteDoc = z
  .array(boundedRecord(z.unknown()))
  .max(5000)
  .refine(
    (d) => new TextEncoder().encode(JSON.stringify(d)).length <= 1_048_576,
    "at most 1 MiB as JSON",
  )
  .meta({ id: "BlockNoteDoc" });
export type BlockNoteDoc = z.infer<typeof BlockNoteDoc>;

const statusKey = z
  .string()
  .regex(/^[a-z][a-z0-9_]*$/)
  .max(LIMITS.key);

/**
 * BOARD-GATES (docs/design/2026-11-02-board-stages-gates-kpis.md §2.1): a column only a person
 * moves cards out of. The card shows its approver Approve · Rework · Reject, each a configured
 * target column; an agent's move out of it is refused (403 `column_locked`), never proposed.
 */
export const ColumnGate = z
  .strictObject({
    needs_person: z.literal(true),
    approve_to: statusKey,
    rework_to: statusKey,
    reject_to: statusKey,
    approver: z
      .union([z.literal("owner"), z.strictObject({ seat_id: Uuid })])
      .optional()
      .describe("Who decides: the card's owner (default) or a named seat"),
  })
  .meta({ id: "ColumnGate" });
export type ColumnGate = z.infer<typeof ColumnGate>;

/** A status of a board's `status_set`; `done` marks terminal statuses (06). */
export const StatusDef = z
  .strictObject({
    key: statusKey,
    name: text(LIMITS.name),
    done: z.boolean(),
    /** Hard dependencies block moving into a status marked `started` or `done` (06). */
    started: z.boolean().optional(),
    color: z.string().max(32).optional(),
    gate: ColumnGate.optional().describe(
      "BOARD-GATES: only a person moves cards out of this column",
    ),
  })
  .meta({ id: "StatusDef" });
export type StatusDef = z.infer<typeof StatusDef>;

/**
 * BOARD-GATES §2.1: a gate's targets are statuses of the set; Rework and Reject leave the column;
 * and no approve chain of more than three gated columns (a card no agent can ever advance).
 */
export function columnGateProblems(
  set: readonly { key: string; gate?: ColumnGate | undefined }[],
): string[] {
  const keys = new Set(set.map((s) => s.key));
  const out: string[] = [];
  for (const s of set) {
    const g = s.gate;
    if (!g) continue;
    for (const k of ["approve_to", "rework_to", "reject_to"] as const)
      if (!keys.has(g[k])) out.push(`${s.key}: ${k} names no status of the board`);
    if (g.rework_to === s.key) out.push(`${s.key}: rework_to must leave the column`);
    if (g.reject_to === s.key) out.push(`${s.key}: reject_to must leave the column`);
    if (g.approve_to === s.key) out.push(`${s.key}: approve_to must leave the column`);
    let n = 1;
    let next = set.find((x) => x.key === g.approve_to);
    const seen = new Set([s.key]);
    while (next?.gate && !seen.has(next.key)) {
      seen.add(next.key);
      n += 1;
      next = set.find((x) => x.key === next?.gate?.approve_to);
    }
    if (n > 3) out.push(`${s.key}: more than three gated columns in a row`);
  }
  return out;
}

/** A board's status set: unique keys, at least one `done` status (06). */
export const StatusSet = z
  .array(StatusDef)
  .min(1)
  .max(30)
  .refine((s) => new Set(s.map((x) => x.key)).size === s.length, "status keys must be unique")
  .refine((s) => s.some((x) => x.done), "at least one status must be done")
  .superRefine((s, ctx) => {
    for (const message of columnGateProblems(s)) ctx.addIssue({ code: "custom", message });
  })
  .meta({ id: "StatusSet" });

export const SpaceSettings = z
  .strictObject({
    default_status_set: StatusSet.optional(),
    board_creators: z.enum(["editors", "admins"]).optional(),
    /**
     * MEMBER-ROLE: what a shared (public, not team or personal) space gives `company#member` in
     * Work: `viewer` (05's default: read, comment) or `editor` (create and edit boards' tasks).
     */
    member_access: z.enum(["viewer", "editor"]).optional(),
    /** M2-02: how long an author may edit a message in this space's channels (06: default 15). */
    message_edit_minutes: z.int().min(1).max(10_080).optional(),
    /**
     * WORKFORCE-SPACES: the space has a Docs item in Work's sidebar though it has no pages yet
     * (the space's "+" › Add docs). A space with pages shows Docs either way; none is automatic.
     */
    docs: z.boolean().optional(),
    /**
     * BOARD-DEFAULT-VIEW: what opening the space shows: one of its boards (on that board's
     * default view), or `overview` (its open tasks by board). Absent or null: its first board.
     */
    opens_on: z
      .union([z.literal("overview"), Uuid])
      .nullable()
      .optional()
      .describe("A board of the space, or `overview`; null or absent: the space's first board"),
    /**
     * BIZ-CLIENT-HEALTH: a client space's account owner seat (a person's or a DE's): it owns the
     * issues the client's health raises, sees the client's health as its KPI, and a DE holding
     * it is woken when the health turns red.
     */
    account_seat_id: Uuid.nullable()
      .optional()
      .describe("A client space's account owner seat; null or absent: none"),
  })
  .meta({ id: "SpaceSettings" });
export type SpaceSettings = z.infer<typeof SpaceSettings>;

export const BoardSettings = z
  .strictObject({
    /** Post a live card in the home channel for each new task (06). */
    live_cards: z.boolean().optional(),
    default_view_id: Uuid.optional(),
    /**
     * WORKFORCE-BOARDVIEW: the view kind tab the board opens on (set by whoever may edit the
     * board; a person's own `board_view` pref wins). Absent or null: the default saved view.
     */
    default_kind: ViewKind.nullable()
      .optional()
      .describe("The tab the board opens on; null or absent: its default saved view"),
    /**
     * T2-BOARD-WAKE / T6-BOARD-NOTE (one setting): how often the board's owner (the lead seat,
     * when a DE holds it) checks it, in minutes — the window its batched board wakes wait for.
     * Edited by both "How it works" (Checks every) and "DE checks". Absent: 15 minutes.
     */
    wake_window_minutes: z
      .int()
      .min(5)
      .max(1440)
      .optional()
      .describe("How often the owning DE checks the board (minutes); absent: 15"),
    /** T2-BOARD-WAKE: at most this many wakes a day (UTC); past it only `now` events wake. */
    wake_daily_cap: z
      .int()
      .min(1)
      .max(500)
      .optional()
      .describe("Most wakes a day for the owning DE (default 48); past it only urgent events"),
    /**
     * HELPER-KPIS: which of the board's status keys play each column role the helper metrics
     * count moves between (`helper-metric-catalog.ts`). Absent role: the status of the same key.
     */
    helper_kpi_columns: z
      .strictObject(
        Object.fromEntries(
          HELPER_KPI_ROLES.map((r) => [
            r,
            z.array(z.string().min(1).max(LIMITS.key)).max(10).optional(),
          ]),
        ) as Record<HelperKpiRole, z.ZodOptional<z.ZodArray<z.ZodString>>>,
      )
      .optional()
      .describe("Status keys per helper-KPI column role; absent: the status of the role's key"),
    /** HELPER-KPIS: the field a critic's verdict is set in, and the values that mean PASS. */
    helper_kpi_verdict: z
      .strictObject({
        field_name: z.string().min(1).max(LIMITS.name),
        pass_values: z.array(z.string().min(1).max(64)).min(1).max(10),
      })
      .optional(),
  })
  .meta({ id: "BoardSettings" });
export type BoardSettings = z.infer<typeof BoardSettings>;

/** `field_def.options` per type (06 §Boards and tasks). */
export const FieldOptions = z
  .strictObject({
    choices: z
      .array(
        z.strictObject({
          key: z.string().max(LIMITS.key),
          name: text(LIMITS.name),
          color: z.string().max(32).optional(),
        }),
      )
      .max(200)
      .optional(),
    currency: Currency.optional(),
    precision: z.int().min(0).max(6).optional(),
    /** `formula`: a domain-kpi formula over other fields (read-only). */
    formula: z.string().max(LIMITS.text).optional(),
    /** `relation`: the board the related tasks live on. */
    relation_board_id: Uuid.optional(),
    /** `rollup`: an aggregation over a relation field's tasks. */
    rollup: z
      .strictObject({
        relation_field_id: Uuid,
        field_id: Uuid.optional(),
        aggregation: z.enum(["count", "sum", "avg", "min", "max", "percent_done"]),
      })
      .optional(),
  })
  .meta({ id: "FieldOptions" });
export type FieldOptions = z.infer<typeof FieldOptions>;

/** A built-in task column or a board field (`field:{uuid}`); WOS-BUNDLE: `tag` (tag ids). */
export const ViewField = z
  .string()
  .max(80)
  .regex(
    /^(title|status|priority|assignee|due_at|start_at|created_at|completed_at|number|tag|field:[0-9a-f-]{36})$/,
  );

export const ViewCondition = z
  .strictObject({
    field: ViewField,
    op: z.enum([
      "eq",
      "neq",
      "in",
      "nin",
      "lt",
      "lte",
      "gt",
      "gte",
      "empty",
      "not_empty",
      "contains",
    ]),
    value: z.unknown().optional(),
  })
  .meta({ id: "ViewCondition" });

/** `view.filter`: every `all` condition and at least one `any` condition (when given). */
export const ViewFilter = z
  .strictObject({
    all: z.array(ViewCondition).max(50).optional(),
    any: z.array(ViewCondition).max(50).optional(),
    include_done: z.boolean().optional(),
  })
  .meta({ id: "ViewFilter" });
export type ViewFilter = z.infer<typeof ViewFilter>;

export const ViewSort = z
  .array(z.strictObject({ field: ViewField, direction: z.enum(["asc", "desc"]) }))
  .max(5)
  .meta({ id: "ViewSort" });
export type ViewSort = z.infer<typeof ViewSort>;

/** Where a task came from (`task.origin_ref`): the id of its source per `origin`. */
export const TaskOriginRef = z
  .strictObject({
    message_id: Uuid.optional(),
    recurring_work_id: Uuid.optional(),
    template_id: Uuid.optional(),
    run_id: Uuid.optional(),
    import_batch_id: Uuid.optional(),
    external_id: z.string().max(256).optional(),
    memory_ref: z.string().max(256).optional(),
    /** DC-10: the failed drift audit (`origin = drift_audit`). */
    drift_audit_id: Uuid.optional(),
    /** MEETINGS: the meeting that captured the to-do (`origin = meeting`) or the issue whose resolution made it (`origin = issue`). */
    meeting_id: Uuid.optional(),
    issue_id: Uuid.optional(),
    /** WOS-BUNDLE: the task this one is a copy of (`origin = duplicate`). */
    task_id: Uuid.optional(),
    /** WEBHOOKS-IN: the incoming webhook and the delivery whose rule made it (`origin = webhook`). */
    inbound_webhook_id: Uuid.optional(),
    inbound_delivery_id: Uuid.optional(),
    /** INC2-INTAKE: the inbound event the task is for (`origin = intake`, and `webhook`). */
    inbound_event_id: Uuid.optional(),
    /** B4-ITEM-TEMPLATES: the board's item template it was made from (`origin = template`). */
    item_template_id: Uuid.optional(),
    /** HELPER-KPIS: the helper (`workforce_agent`) the creating DE named (`helper`). */
    helper_id: Uuid.optional(),
    /** CORE-REVERT: the incident whose revert left this follow-up to handle by hand. */
    incident_id: Uuid.optional(),
  })
  .meta({ id: "TaskOriginRef" });
export type TaskOriginRef = z.infer<typeof TaskOriginRef>;

// Spaces and chat -------------------------------------------------------------------------------

export const Space = z
  .object({
    company_id: Uuid,
    id: Uuid,
    name: z.string(),
    kind: SpaceKind,
    team_id: Uuid.nullable(),
    owner_membership_id: Uuid.nullable(),
    icon: z.string().nullable(),
    description: z.string().nullable(),
    is_private: z.boolean(),
    settings: SpaceSettings,
    position: z
      .int()
      .describe("BOARD-ORDER: the company's order of its spaces (lower first; ties by name)"),
    archived_at: Timestamp.nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "Space" });
export type Space = z.infer<typeof Space>;

export const Channel = z
  .object({
    company_id: Uuid,
    id: Uuid,
    space_id: Uuid.nullable(),
    kind: ChannelKind,
    name: z.string(),
    description: z.string().nullable(),
    board_id: Uuid.nullable(),
    team_id: Uuid.nullable(),
    is_private: z.boolean(),
    is_agent_room: z.boolean(),
    /** Members of the company are in it without browsing (`#general`; CHAT-UX). */
    is_default: z.boolean(),
    taken_over_by_membership_id: Uuid.nullable(),
    taken_over_at: Timestamp.nullable(),
    posting_policy: ChannelPostingPolicy,
    /** CHAT-CONTROLS: who may open topics (`admins`: the channel's managers only). */
    topic_policy: ChannelPostingPolicy,
    /** CHAT-SWITCH-A: who may use `@channel` / `@here` (`admins`: the channel's managers only). */
    broadcast_policy: ChannelPostingPolicy,
    /** CHAT-CONTROLS: the notification level a new member starts with. */
    default_notify_level: ChannelNotifyLevel,
    dm_key: z.string().nullable(),
    archived_at: Timestamp.nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "Channel" });
export type Channel = z.infer<typeof Channel>;

export const ChannelMember = z
  .object({
    company_id: Uuid,
    channel_id: Uuid,
    membership_id: Uuid,
    role: ChannelMemberRole,
    muted: z.boolean(),
    notify_level: ChannelNotifyLevel,
    last_read_message_id: Uuid.nullable(),
    last_read_at: Timestamp.nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "ChannelMember" });
export type ChannelMember = z.infer<typeof ChannelMember>;

export const Topic = z
  .object({
    company_id: Uuid,
    id: Uuid,
    channel_id: Uuid,
    name: z.string(),
    status: TopicStatus,
    is_agent_room: z.boolean(),
    taken_over_by_membership_id: Uuid.nullable(),
    taken_over_at: Timestamp.nullable(),
    /** CHAT-UX: pinned topics lead their channel's list (any poster pins; managers unpin). */
    pinned_at: Timestamp.nullable(),
    /** CHAT-UX: archived by a manager; hidden from the lists unless a manager asks for them. */
    archived_at: Timestamp.nullable(),
    last_message_at: Timestamp.nullable(),
    message_count: z.int().min(0),
    /**
     * CHAT-TOPICS-SEPARATE: only its members see a private topic (listed, searched or by id);
     * an open one is listed to the channel and joined with one click.
     */
    is_private: z.boolean(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "Topic" });
export type Topic = z.infer<typeof Topic>;

/** CHAT-TOPICS-SEPARATE: a member of a topic (who is notified of it and counts it unread). */
export const TopicMember = z
  .object({
    company_id: Uuid,
    topic_id: Uuid,
    membership_id: Uuid,
    via: TopicMemberVia,
    last_read_message_id: Uuid.nullable(),
    last_read_at: Timestamp.nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "TopicMember" });
export type TopicMember = z.infer<typeof TopicMember>;

export const Thread = z
  .object({
    company_id: Uuid,
    id: Uuid,
    kind: ThreadKind,
    subject_id: Uuid,
    last_message_at: Timestamp.nullable(),
    message_count: z.int().min(0),
    ...stamps,
  })
  .meta({ id: "Thread" });
export type Thread = z.infer<typeof Thread>;

const { actor_user_id: _noUser, ...messageActor } = ActorColumns.shape;

/** A message with the full actor contract (02 Conventions); `body` is null once deleted. */
export const Message = z
  .object({
    company_id: Uuid,
    created_at: Timestamp,
    id: Uuid,
    thread_id: Uuid,
    author_membership_id: Uuid.nullable(),
    author_kind: MessageAuthorKind,
    body: BlockNoteDoc,
    body_text: z.string(),
    mentions: z.array(z.string()),
    card_refs: z.array(z.string()),
    parent_message_id: Uuid.nullable(),
    client_id: z.string().nullable(),
    edited_at: Timestamp.nullable(),
    deleted_at: Timestamp.nullable(),
    pinned_at: Timestamp.nullable(),
    ...messageActor,
  })
  .meta({ id: "Message" });
export type Message = z.infer<typeof Message>;

export const Reaction = z
  .object({
    company_id: Uuid,
    message_id: Uuid,
    membership_id: Uuid,
    emoji: z.string(),
    created_at: Timestamp,
  })
  .meta({ id: "Reaction" });
export type Reaction = z.infer<typeof Reaction>;

/** A member's bookmark of a message (M2-02); only its member ever reads it. */
export const MessageBookmark = z
  .object({
    company_id: Uuid,
    membership_id: Uuid,
    message_id: Uuid,
    message_created_at: Timestamp,
    thread_id: Uuid,
    created_at: Timestamp,
  })
  .meta({ id: "MessageBookmark" });
export type MessageBookmark = z.infer<typeof MessageBookmark>;

/** THREAD-READS: a member's read position in one reply thread and whether it follows it. */
export const ThreadRead = z
  .object({
    company_id: Uuid,
    membership_id: Uuid,
    root_message_id: Uuid,
    last_read_reply_id: Uuid.nullable(),
    last_read_at: Timestamp.nullable(),
    following: z.boolean().nullable(),
    /** CHAT-THREAD-AGENTS: an agent's participation in the reply thread (its first reason). */
    participant: ThreadParticipantRole.nullable(),
    participant_since: Timestamp.nullable(),
    muted_at: Timestamp.nullable(),
    last_posted_at: Timestamp.nullable(),
    reply_to_channel: z.boolean(),
    created_at: Timestamp,
    updated_at: Timestamp,
  })
  .meta({ id: "ThreadRead" });
export type ThreadRead = z.infer<typeof ThreadRead>;

/**
 * CHAT-FILES: the source of a chat file saved elsewhere (a copy: its own row and its own bytes,
 * so deleting the message, thread or channel never removes it).
 */
export const AttachmentProvenance = z
  .object({
    kind: z.literal("chat_message"),
    source_attachment_id: Uuid,
    message_id: Uuid,
    thread_id: Uuid,
    sender_membership_id: Uuid,
    sender_name: z.string(),
    sent_at: Timestamp,
    saved_by_membership_id: Uuid,
    saved_at: Timestamp,
  })
  .meta({ id: "AttachmentProvenance" });
export type AttachmentProvenance = z.infer<typeof AttachmentProvenance>;

export const Attachment = z
  .object({
    company_id: Uuid,
    id: Uuid,
    object_key: z.string(),
    file_name: z.string(),
    mime: z.string(),
    bytes: z.int().min(1),
    uploader_membership_id: Uuid,
    subject_kind: AttachmentSubjectKind,
    subject_id: Uuid,
    state: AttachmentState,
    /** TASK-FILES: the task's folder it is in (null = the root). */
    folder_id: Uuid.nullable(),
    /** TASK-FILES: shown on the board card (up to 4 covers). */
    is_cover: z.boolean(),
    /** TASK-FILES: the task the file shows under (its subject, or its comment's task). */
    task_id: Uuid.nullable(),
    /** CHAT-FILES: a chat file's thread (null for a task's or a page's own files). */
    thread_id: Uuid.nullable(),
    /** CHAT-FILES: where a saved copy of a chat file came from (null: not a copy). */
    provenance: AttachmentProvenance.nullable(),
    /** IMAGE-PREVIEWS: an image's pixel size and when its thumbnail and preview were made. */
    width: z.int().nullable(),
    height: z.int().nullable(),
    derivatives_at: Timestamp.nullable(),
    /** TASK-FILES: in the trash since (restorable for 30 days), else null. */
    deleted_at: Timestamp.nullable(),
    deleted_by_membership_id: Uuid.nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "Attachment" });
export type Attachment = z.infer<typeof Attachment>;

/** TASK-FILES: a folder of a task's files (any depth; Work OS `attachment_folders`). */
export const AttachmentFolder = z
  .object({
    company_id: Uuid,
    id: Uuid,
    task_id: Uuid,
    name: z.string(),
    parent_id: Uuid.nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "AttachmentFolder" });
export type AttachmentFolder = z.infer<typeof AttachmentFolder>;

export const Follow = z
  .object({
    company_id: Uuid,
    membership_id: Uuid,
    subject_kind: FollowSubjectKind,
    subject_id: Uuid,
    level: FollowLevel,
    ...actor,
    ...stamps,
  })
  .meta({ id: "Follow" });
export type Follow = z.infer<typeof Follow>;

/**
 * BOARD-GATES (§2.2): the person-only gate of the column a task is in, as the caller sees it —
 * where Approve · Rework · Reject send it and whether the caller decides it. One shape for the
 * card (`tasks.reviewGate`) and the Inbox list (`review_gate` on a task row).
 */
export const ColumnGateView = z
  .object({
    column: z.string(),
    column_name: z.string(),
    approve_to: z.object({ key: z.string(), name: z.string() }),
    rework_to: z.object({ key: z.string(), name: z.string() }),
    reject_to: z.object({ key: z.string(), name: z.string() }),
    approver_seat_id: Uuid.nullable(),
    approver_name: z.string().nullable().describe("Who decides (null: nobody found)"),
    can_decide: z.boolean(),
  })
  .meta({ id: "ColumnGateView" });
export type ColumnGateView = z.infer<typeof ColumnGateView>;

export const InboxItem = z
  .object({
    company_id: Uuid,
    id: Uuid,
    membership_id: Uuid,
    kind: InboxItemKind,
    subject_kind: InboxSubjectKind,
    subject_id: Uuid,
    message_id: Uuid.nullable(),
    source_event_id: Uuid,
    coalesce_key: z.string().nullable(),
    count: z.int().min(1),
    summary: z.string(),
    read_at: Timestamp.nullable(),
    done_at: Timestamp.nullable(),
    /** M2-04: out of the inbox until then (snooze). */
    snoozed_until: Timestamp.nullable(),
    /**
     * SEARCH-NOTIF: the item asks for a desktop (browser) notification — the person's preference
     * for its kind, outside their quiet hours, when it arrived.
     */
    desktop: z.boolean(),
    /** INBOX-PREFS: the notification type it was routed as (null: not routable, or older). */
    category: NotificationCategory.nullable(),
    /** INBOX-PREFS: who it is from — who is waiting, on a waiting item (null: the system). */
    from_membership_id: Uuid.nullable(),
    /** INBOX-PREFS: a waiting item's one gentle reminder. */
    nudged_at: Timestamp.nullable(),
    /** INBOX-PREFS: when a waiting item was handed on (`inbox_item.escalation_due`). */
    escalated_at: Timestamp.nullable(),
    ...stamps,
  })
  .meta({ id: "InboxItem" });
export type InboxItem = z.infer<typeof InboxItem>;

/** SEARCH-NOTIF: one inbox kind's channels as stored (an absent key: the kind's default). */
export const NotificationChannelsRow = z
  .strictObject({
    inbox: z.boolean().optional(),
    email: z.boolean().optional(),
    desktop: z.boolean().optional(),
  })
  .meta({ id: "NotificationChannelsRow" });

/** SEARCH-NOTIF: a person's stored notification preferences in one company (the overrides). */
export const NotificationPref = z
  .object({
    company_id: Uuid,
    membership_id: Uuid,
    types: z.partialRecord(InboxItemKind, NotificationChannelsRow),
    /** Minutes after local midnight; both null: no quiet hours. */
    quiet_start: z.int().min(0).max(1439).nullable(),
    quiet_end: z.int().min(0).max(1439).nullable(),
    time_zone: z.string().nullable(),
    digest_frequency: DigestFrequency,
    digest_sent_at: Timestamp.nullable(),
    /** INBOX-PREFS: the person's preset (null: the company's default). */
    preset: NotificationPreset.nullable(),
    /** INBOX-PREFS: the person's edits over the preset, per row. */
    routes: z.partialRecord(
      NotificationCategory,
      z.strictObject({
        inbox: z.boolean().optional(),
        alert: z.boolean().optional(),
        email: z.boolean().optional(),
        digest: z.boolean().optional(),
      }),
    ),
    paused_until: Timestamp.nullable(),
    /** INBOX-PREFS: `task:<id>` / `agent:<membership id>` → level. */
    overrides: z.record(z.string(), z.enum(["all", "mentions", "mute"])),
    /** DE-REPLY-STYLE: how long the person wants DEs' chat replies (null: short). */
    reply_length: ReplyLength.nullable(),
    /** DE-QUICK-ACK: DEs' instant first reply (null: on). */
    quick_replies: z.boolean().nullable(),
    /** CHAT-COST-PILLS: show the cost label on agents' chat messages (null: on). */
    show_costs: z.boolean().nullable(),
    ...stamps,
  })
  .meta({ id: "NotificationPref" });
export type NotificationPref = z.infer<typeof NotificationPref>;

/** CHAT-SWITCH-A: a person's custom status (emoji, text, expiry) and reported time zone. */
export const MemberStatusRow = z
  .object({
    company_id: Uuid,
    membership_id: Uuid,
    emoji: z.string().min(1).max(32).nullable(),
    text: z.string().min(1).max(100).nullable(),
    expires_at: Timestamp.nullable(),
    time_zone: z.string().min(1).max(64).nullable(),
    ...stamps,
  })
  .meta({ id: "MemberStatusRow" });
export type MemberStatusRow = z.infer<typeof MemberStatusRow>;

// CHAT-SWITCH-B ----------------------------------------------------------------------------------

/** A question put to a DE about chat history, with the messages it may read and cite. */
export const ChatAskRow = z
  .object({
    company_id: Uuid,
    id: Uuid,
    asker_membership_id: Uuid,
    de_membership_id: Uuid,
    question: z.string().min(1).max(500),
    query: z.record(z.string(), z.string()),
    sources: z.array(z.object({ message_id: Uuid, created_at: Timestamp })),
    state: ChatAskState,
    answer: z.string().min(1).max(8000).nullable(),
    citations: z.array(Uuid),
    failure: z.string().min(1).max(200).nullable(),
    wakeup_id: Uuid.nullable(),
    answered_at: Timestamp.nullable(),
    ...stamps,
  })
  .meta({ id: "ChatAskRow" });

/** A channel's incoming webhook (the token itself is never stored). */
export const ChannelWebhookRow = z
  .object({
    company_id: Uuid,
    id: Uuid,
    channel_id: Uuid.nullable(),
    de_membership_id: Uuid.nullable(),
    name: z.string().min(1).max(80),
    token_hash: z.string(),
    token_hint: z.string().length(4),
    created_by_membership_id: Uuid,
    post_count: z.int().min(0),
    last_used_at: Timestamp.nullable(),
    rotated_at: Timestamp.nullable(),
    revoked_at: Timestamp.nullable(),
    ...stamps,
  })
  .meta({ id: "ChannelWebhookRow" });

/** A message written to send later. */
export const ScheduledMessageRow = z
  .object({
    company_id: Uuid,
    id: Uuid,
    author_membership_id: Uuid,
    thread_id: Uuid,
    channel_id: Uuid,
    parent_message_id: Uuid.nullable(),
    body: BlockNoteDoc,
    body_text: z.string().max(40000),
    mentions: z.array(z.string()),
    client_id: z.string().min(1).max(128).nullable(),
    addressee: z.string().nullable(),
    broadcast: ChatBroadcast.nullable(),
    send_at: Timestamp,
    state: ScheduledMessageState,
    message_id: Uuid.nullable(),
    failure: z.string().min(1).max(200).nullable(),
    sent_at: Timestamp.nullable(),
    ...stamps,
  })
  .meta({ id: "ScheduledMessageRow" });

/** A link's cached Open Graph card (or that fetching it failed). */
export const LinkPreviewRow = z
  .object({
    company_id: Uuid,
    url_hash: z.string(),
    url: z.string().min(1).max(2048),
    status: LinkPreviewStatus,
    title: z.string().max(300).nullable(),
    description: z.string().max(500).nullable(),
    image_url: z.string().max(2048).nullable(),
    site_name: z.string().max(100).nullable(),
    fetched_at: Timestamp,
  })
  .meta({ id: "LinkPreviewRow" });

/** A link card removed from a message by its author. */
export const MessagePreviewHideRow = z
  .object({
    company_id: Uuid,
    message_id: Uuid,
    url_hash: z.string(),
    hidden_by_membership_id: Uuid,
    created_at: Timestamp,
  })
  .meta({ id: "MessagePreviewHideRow" });

/** CHAT-PRESENCE: a DM a member closed in their own list. */
export const DmClosureRow = z
  .object({
    company_id: Uuid,
    membership_id: Uuid,
    channel_id: Uuid,
    closed_at: Timestamp,
  })
  .meta({ id: "DmClosureRow" });

/** CHAT-PRESENCE: a DE's failed run someone acknowledged. */
export const RunFailureAckRow = z
  .object({
    company_id: Uuid,
    run_id: Uuid,
    de_membership_id: Uuid,
    acked_by_membership_id: Uuid,
    acked_at: Timestamp,
  })
  .meta({ id: "RunFailureAckRow" });

// Boards and tasks ------------------------------------------------------------------------------

export const Board = z
  .object({
    company_id: Uuid,
    id: Uuid,
    space_id: Uuid,
    name: z.string(),
    home_channel_id: Uuid.nullable(),
    status_set: z.array(StatusDef),
    settings: BoardSettings,
    digest_interval: BoardDigestInterval,
    position: z
      .int()
      .describe(
        "BOARD-ORDER: the board's place among its space's boards (lower first; ties by name)",
      ),
    color: BoardColor.nullable().describe("BOARD-ORDER: null keeps the tint picked by the name"),
    lead_seat_id: Uuid.nullable().describe("BOARD-ORDER: the seat that leads the board"),
    page_id: Uuid.nullable().describe(
      "T6-BOARD-NOTE: the board's “How this board works” note page (null until written)",
    ),
    archived_at: Timestamp.nullable(),
    version: z.int().min(1),
    ...actor,
    ...stamps,
  })
  .meta({ id: "Board" });
export type Board = z.infer<typeof Board>;

export const FieldDef = z
  .object({
    company_id: Uuid,
    id: Uuid,
    board_id: Uuid,
    name: z.string(),
    type: FieldType,
    options: FieldOptions,
    audience: FieldAudience,
    requires_approval: z.boolean(),
    position: z.int(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "FieldDef" });
export type FieldDef = z.infer<typeof FieldDef>;

export const View = z
  .object({
    company_id: Uuid,
    id: Uuid,
    board_id: Uuid,
    kind: ViewKind,
    name: z.string(),
    filter: ViewFilter,
    sort: ViewSort,
    group_by: z.string().nullable(),
    visible_fields: z.array(z.string()),
    /** T4-VIEWS-MEDIA: the date a calendar or timeline places tasks by; null: `due_at`. */
    date_field: z
      .string()
      .nullable()
      .describe("`due_at`, `start_at` or `field:{uuid}` of a date field; null: `due_at`"),
    /** T4-VIEWS-MEDIA: this view's card fields; null: the fields marked `show_on_card`. */
    card_fields: z
      .array(z.string())
      .nullable()
      .describe("Fields on this view's cards, in order; null: the fields marked show-on-card"),
    is_default: z.boolean(),
    position: z.int(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "View" });
export type View = z.infer<typeof View>;

/**
 * B4-ITEM-TEMPLATES: what a board's item template fills a new task with (`POST tasks
 * {template_id}`). Anything the create names itself wins over the template. Field ids are the
 * board's; dates are offsets from the day the task is made.
 */
export const ItemTemplatePayload = z
  .strictObject({
    title: text(500),
    status: z.string().max(64).optional().describe("A status key of the board"),
    priority: z.int().min(0).max(4).optional(),
    estimate_minutes: z.int().min(0).nullable().optional(),
    assignee_seat_ids: z.array(Uuid).max(20).optional(),
    fields: boundedRecord(z.unknown(), LIMITS.list, Uuid).optional(),
    body: BlockNoteDoc.optional().describe("The description (the task's page body)"),
    checklist: z.array(text(500)).max(200).optional(),
    tag_ids: z.array(Uuid).max(50).optional(),
    due_in_days: z.int().min(0).max(3650).optional().describe("Due this many days after creation"),
    start_in_days: z.int().min(0).max(3650).optional(),
  })
  .meta({ id: "ItemTemplatePayload" });
export type ItemTemplatePayload = z.infer<typeof ItemTemplatePayload>;

/** B4-ITEM-TEMPLATES: a board's item template (save a task as one; create tasks from it). */
export const ItemTemplate = z
  .object({
    company_id: Uuid,
    id: Uuid,
    board_id: Uuid,
    name: z.string(),
    description: z.string().nullable(),
    payload: ItemTemplatePayload,
    source_task_id: Uuid.nullable().describe("The task it was saved from, if any"),
    position: z.int(),
    archived_at: Timestamp.nullable(),
    version: z.int().min(1),
    ...actor,
    ...stamps,
  })
  .meta({ id: "ItemTemplate" });
export type ItemTemplate = z.infer<typeof ItemTemplate>;

export const Task = z
  .object({
    company_id: Uuid,
    id: Uuid,
    board_id: Uuid,
    number: z.int().min(1),
    title: z.string(),
    status: z.string(),
    priority: z.int().min(0).max(4),
    parent_task_id: Uuid.nullable(),
    page_id: Uuid.nullable(),
    thread_id: Uuid.nullable(),
    due_at: Timestamp.nullable(),
    start_at: Timestamp.nullable(),
    estimate_minutes: z.int().min(0).nullable(),
    created_by_seat_id: Uuid.nullable(),
    completed_at: Timestamp.nullable(),
    origin: TaskOrigin,
    origin_ref: TaskOriginRef.nullable(),
    client_id: z.string().nullable(),
    restricted: z.boolean(),
    version: z.int().min(1),
    ...actor,
    ...stamps,
  })
  .meta({ id: "Task" });
export type Task = z.infer<typeof Task>;

export const TaskAssignee = z
  .object({
    company_id: Uuid,
    task_id: Uuid,
    seat_id: Uuid,
    ...actor,
    created_at: Timestamp,
  })
  .meta({ id: "TaskAssignee" });
export type TaskAssignee = z.infer<typeof TaskAssignee>;

export const TaskFieldValue = z
  .object({
    company_id: Uuid,
    task_id: Uuid,
    field_def_id: Uuid,
    value: z.unknown(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "TaskFieldValue" });
export type TaskFieldValue = z.infer<typeof TaskFieldValue>;

/** DOCS-KB: a task's named checklist; items belong to one. */
export const TaskChecklist = z
  .object({
    company_id: Uuid,
    id: Uuid,
    task_id: Uuid,
    title: z.string(),
    position: z.int(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "TaskChecklist" });
export type TaskChecklist = z.infer<typeof TaskChecklist>;

export const TaskChecklistItem = z
  .object({
    company_id: Uuid,
    id: Uuid,
    task_id: Uuid,
    checklist_id: Uuid,
    text: z.string(),
    done_at: Timestamp.nullable(),
    done_by_seat_id: Uuid.nullable(),
    assignee_seat_id: Uuid.nullable(),
    due_at: Timestamp.nullable(),
    position: z.int(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "TaskChecklistItem" });
export type TaskChecklistItem = z.infer<typeof TaskChecklistItem>;

export const TaskDependency = z
  .object({
    company_id: Uuid,
    task_id: Uuid,
    depends_on_task_id: Uuid,
    kind: TaskDependencyKind,
    ...actor,
    created_at: Timestamp,
  })
  .meta({ id: "TaskDependency" });
export type TaskDependency = z.infer<typeof TaskDependency>;
